import React, { useEffect } from "react";
import styled from "styled-components";

import { useSelector } from "react-redux";
import { translations } from "../../utils/translations";
import Button from "./core-components/Button.tsx";
import InputField from "./core-components/InputField.tsx";
import RadioButtonInput from "./core-components/RatioButtonInput.tsx";
import { useAuth } from "../../hooks/useAuth.jsx";

import {
  calculateDiscountedPrice,
  getUserIdFromToken,
  getUserRoleFromToken,
} from "../../utils/utils.ts";
import { toast, ToastContainer } from "react-toastify";
import { useQuery } from "@tanstack/react-query";
import SimpleDropdown from "./core-components/SimpleDropdown.tsx";
import {
  addNewAddress,
  addNewOrder,
  sendEmail,
  updateUserAddress,
} from "../../services/orderService.js";
import {
  orderTaxes,
  orderTaxesNames,
  paymentStatus as paymentStatusOptions,
  regions,
  roles,
} from "../../utils/constants.ts";
import { getAccountDetails } from "../../services/authService.js";
import { useNavigate } from "react-router-dom";
import Loader from "./core-components/Loader.tsx";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  padding: 16px;
  border: 2px solid var(--background-yellow);
  border-radius: 8px;
  justify-content: space-between;
`;

const Title = styled.p`
  width: 100%;
  border-bottom: 2px solid var(--background-yellow);
  font-size: 20px;
  padding-bottom: 16px;
  font-family: Georgia, serif;
  color: var(--secondary-font-color);
  margin: 0;
`;

const RowHeader = styled.p`
  border-top: 1px solid var(--background-yellow);
  border-bottom: 1px solid var(--background-yellow);
  padding: 14px 0;
  font-size: 16px;

  font-family: Georgia, serif;
  color: var(--tertiary-font-color);
  margin: 0;
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 16px;
`;

const SummaryRow = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const SummarySubPrice = styled.div`
  margin-top: 16px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  font-size: 16px;
  padding-bottom: 12px;
  font-family: Verdana, sans-serif;
  color: var(--tertiary-font-color);
`;

const SummaryItems = styled.div`
  width: 100%;
  text-align: right;
  font-size: 12px;
  padding-bottom: 12px;
  font-family: Verdana, sans-serif;
  color: var(--tertiary-font-color);
  margin: 0;
`;
const SummaryPrice = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  font-size: 20px;
  padding-bottom: 16px;
  font-family: Verdana, sans-serif;
  color: var(--secondary-font-color);
  margin: 0;
`;

const CheckboxLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: left;
  gap: 8px;
  font-size: 0.9rem;
  font-family: Verdana, sans-serif;
  color: var(--secondary-font-color);
  width: 100%;
`;

const ClientDetails = styled.div`
  width: 100%;
  font-size: 14px;
  font-family: Verdana, sans-serif;
  color: var(--tertiary-font-color);
  p {
    margin: 0;
    margin-bottom: 4px;
  }
`;

const LineAddress = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
`;

const LoginComponent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const ClientDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 16px;
`;

interface Props {
  totalPrice: number;
  itemsNumber: number;
  products: any[];
  placeOrderSuccess: (order: any) => void;
}

const notify = (msg) =>
  toast.error(msg, {
    position: "top-right",
    autoClose: 2000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: false,
    draggable: false,
    progress: undefined,
    theme: "light",
  });

const CheckoutSummary: React.FC<Props> = ({
  totalPrice,
  itemsNumber,
  products,
  placeOrderSuccess,
}) => {
  const navigate = useNavigate();
  const languageCode = useSelector((state: any) => state.settings.languageCode);
  const [message, setMessage] = React.useState("");
  const [address, setAddress] = React.useState("");
  const [county, setCounty] = React.useState("");
  const [city, setCity] = React.useState("");
  const [saveAddress, setSaveSddress] = React.useState(true); //salveaza si fa update la adresa noua in cont
  const [newAddress, setNewAddress] = React.useState(false); //adauga o adresa noua pentru order
  const [paymentOption, setPaymentOption] = React.useState("");
  const [shippingOption, setShippingOption] = React.useState("");
  const [isPlacingOrder, setIsPlacingOrder] = React.useState(false);
  const [total, setTotal] = React.useState(totalPrice);
  const [terms, setTerms] = React.useState(false);
  const { token } = useAuth();
  const userId = getUserIdFromToken(token);
  const userRole = getUserRoleFromToken(token);

  const { data: accountDetails } = useQuery({
    queryKey: ["details", userId],
    queryFn: () => getAccountDetails(userId),
    enabled: !!userId && Number(userId) > 0,
    retry: false,
  });

  useEffect(() => {
    let updatePrice = Number(totalPrice);
    if (shippingOption === orderTaxesNames.shipping) {
      updatePrice += orderTaxes.shipping;
    }
    if (paymentOption === orderTaxesNames.cash) {
      updatePrice += orderTaxes.cash;
    }

    setTotal(updatePrice);
  }, [shippingOption, paymentOption, totalPrice, setTotal]);

  useEffect(() => {
    if (accountDetails?.address) {
      setNewAddress(false);
    } else {
      setNewAddress(true);
    }
  }, [setNewAddress, accountDetails]);

  const handleAddressSaving = async () => {
    //if newAddress is true => creez o adresa noua si adaug id in order
    // else => folosesc adresa existenta din user

    if (newAddress) {
      const addressData = {
        county: county,
        city: city,
        street: address,
      };
      try {
        const address = await addNewAddress(addressData);

        if (saveAddress) {
          try {
            await updateUserAddress(userId, address.addressID);
          } catch (error) {
            console.error("Error updateing address:", error);
          }
        }

        return address.addressID.toString();
      } catch (error) {
        notify(translations[languageCode]["checkout-summary-order-error"]);
        console.error("Error creating address:", error);
      }
    } else {
      return accountDetails?.addressID.toString();
    }
  };

  const handleSend = async (mail) => {
    try {
      await sendEmail(mail);
    } catch (error) {
      console.error("Error sending email", error);
    }
  };

  const handlePlaceOrder = async () => {
    if (!terms) {
      notify(translations[languageCode]["checkout-summary-terms-error"]);
    } else if (!paymentOption) {
      notify(translations[languageCode]["checkout-summary-payment-error"]);
    } else if (!shippingOption) {
      notify(translations[languageCode]["checkout-summary-shipping-error"]);
    } else if ((!address || !county || !city) && newAddress) {
      notify(translations[languageCode]["checkout-summary-address-error"]);
    } else {
      setIsPlacingOrder(true);
      const addresssID = await handleAddressSaving();

      const orderData = {
        clientID: userId,
        addressID: addresssID,
        message: message,
        paymentOption: paymentOption,
        paymentStatus: paymentStatusOptions.UNPAID,
        shippingOption: shippingOption,
        totalPrice: total,
        orderDetails: products.map((product) => ({
          productID: product.id,
          productCantity: product.quantity,
          salePrice: calculateDiscountedPrice(product.price, product.discount),
        })),
      };
      try {
        const newOrder = await addNewOrder(orderData, token);

        await handleSend({
          subject: "Comanda nouă #" + newOrder.orderID,
          toAddress: accountDetails?.email,
          content: `<div>
          <h3>Felicitări ! Ai plasat cea mai tare comandă !</h3>
          <p>
            Punem imprimantele la lucru, iar când comanda ta va fi gata te vom
            contacta.
          </p>
         <p>Mulțumim, FiloPrint !</p>
                  <img
                    src="https://filoprint.ro/images/header-logo.png"
                    alt="Logo"
                    width="100"
                  />
                  <div>Telefon: 0724702368</div>
                  <div>
                    Site: https://filoprint.ro/
                  </div>
        </div>
          `,
        });

        await handleSend({
          subject: "Comanda nouă #" + newOrder.orderID,
          toAddress: "contact@filoprint.ro",
          content: `<div>
          <h3>Ai o comandă nouă pe site!</h3>
          <p>
           Verifică secțiunea de comenzi pentru detalii.
          </p>
            <h4>
          Plata: ${newOrder.paymentOption}, Transport: ${newOrder.shippingOption}, Suma: ${newOrder.totalPrice} lei
          </h4>
       <p>Mulțumim, FiloPrint !</p>
                  <img
                    src="https://filoprint.ro/images/header-logo.png"
                    alt="Logo"
                    width="100"
                  />
                  <div>Telefon: 0724702368</div>
                  <div>
                    Site: https://filoprint.ro/
                  </div>
        </div>
          `,
        });
        setIsPlacingOrder(false);
        placeOrderSuccess(newOrder);
      } catch (error) {
        setIsPlacingOrder(false);
        notify(translations[languageCode]["checkout-summary-order-error"]);
        console.error("Error creating order:", error);
      }
    }
  };
  const addressComponent = () => {
    return newAddress || //daca a fost bigat noua adresa sau nu exista adresa deloc
      !accountDetails?.address ||
      !accountDetails?.addressID ? (
      <>
        <LineAddress>
          <SimpleDropdown
            values={regions}
            name="county"
            label={translations[languageCode]["checkout-summary-county"]}
            onChange={({ value, label }) => setCounty(value)}
          />
          <InputField
            name="city"
            type="text"
            label={translations[languageCode]["checkout-summary-city"]}
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder={translations[languageCode]["checkout-summary-city"]}
            isRequired
          />
        </LineAddress>

        <InputField
          name="address"
          type="text"
          label={translations[languageCode]["checkout-summary-address"]}
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder={translations[languageCode]["checkout-summary-address"]}
          isRequired
        />

        <CheckboxLabel>
          <input
            type="checkbox"
            name="save-address"
            value="save-address"
            checked={saveAddress}
            onChange={(value) => {
              setSaveSddress(value.target.checked);
            }}
          />
          {translations[languageCode]["checkout-summary-save-address"]}
        </CheckboxLabel>
      </>
    ) : (
      <>
        <p
          style={{ margin: "0" }}
        >{`Romania, ${accountDetails?.address?.county},  ${accountDetails?.address?.city},  ${accountDetails?.address?.street}`}</p>
      </>
    );
  };

  return (
    <Container>
      <Title>{translations[languageCode]["checkout-summary-order"]}</Title>
      <Row>
        <ClientDetailsContainer>
          {userRole == roles.CLIENT && userId && Number(userId) > 0 ? (
            <>
              <RowHeader style={{ borderTop: "none", paddingTop: "0" }}>
                {translations[languageCode]["checkout-summary-billing"]}
              </RowHeader>
              <ClientDetails>
                <p>{`${accountDetails?.firstName} ${accountDetails?.lastName}, ${accountDetails?.email}`}</p>
                <p>{`${accountDetails?.phoneNumber}`}</p>
              </ClientDetails>

              {addressComponent()}
              <CheckboxLabel>
                <input
                  type="checkbox"
                  name="new-address"
                  value="new-address"
                  checked={newAddress}
                  onChange={(value) => {
                    setNewAddress(value.target.checked);
                  }}
                />
                {translations[languageCode]["checkout-summary-new-address"]}
              </CheckboxLabel>
            </>
          ) : (
            <LoginComponent>
              {translations[languageCode]["checkout-summary-login"]}
              <Button
                id="order-login"
                onClick={() => navigate("/login")}
                text={"Login"}
                backgroundColor={"var(--secondary-green)"}
                color="white"
                isFullWidth
              />
              <button
                style={{
                  border: "none",
                  background: "none",
                  textAlign: "left",
                  cursor: "pointer",
                }}
                onClick={() => navigate("/register")}
              >
                {translations[languageCode]["login-register-message"]}
              </button>
            </LoginComponent>
          )}
        </ClientDetailsContainer>
      </Row>
      <Row>
        <RowHeader>
          {translations[languageCode]["checkout-summary-shipping"]}
        </RowHeader>
        <RadioButtonInput
          name="shipping"
          setSelectedValue={setShippingOption}
          options={[
            {
              value: orderTaxesNames.pickUp,
              label: translations[languageCode]["checkout-summary-shipping-1"],
            },
             {
              value: orderTaxesNames.pickUp,
              label: translations[languageCode]["checkout-summary-shipping-3"],
            },
            {
              value: orderTaxesNames.shipping,
              label: translations[languageCode]["checkout-summary-shipping-2"],
            },
          ]}
        />
      </Row>

      <Row>
        <RowHeader>
          {translations[languageCode]["checkout-summary-payment"]}
        </RowHeader>
        <RadioButtonInput
          name="payment"
          setSelectedValue={setPaymentOption}
          options={[
            {
              value: orderTaxesNames.cash,
              label: translations[languageCode]["checkout-summary-payment-1"],
            },
            {
              value: orderTaxesNames.card,
              label: translations[languageCode]["checkout-summary-payment-2"],
            },
            {
              value: orderTaxesNames.transfer,
              label: translations[languageCode]["checkout-summary-payment-3"],
            },
          ]}
        />
      </Row>

      <SummaryRow>
        <RowHeader>
          {translations[languageCode]["checkout-summary-title"]}
        </RowHeader>
        <InputField
          name="code"
          type="textarea"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={translations[languageCode]["reviews-msg"]}
        />
        <SummarySubPrice>
          {translations[languageCode]["checkout-summary-subtotal"]}{" "}
          <div>
            {totalPrice} {" lei"}
          </div>
        </SummarySubPrice>
        <SummaryItems>
          {itemsNumber} {translations[languageCode]["checkout-summary-items"]}
        </SummaryItems>
        <SummaryPrice>
          {translations[languageCode]["checkout-summary-total"]}
          <div>
            {total} {" lei"}
          </div>
        </SummaryPrice>
      </SummaryRow>
      <CheckboxLabel>
        <input
          type="checkbox"
          name="terms"
          value="terms"
          onChange={(value) => {
            setTerms(value.target.checked);
          }}
        />
        {translations[languageCode]["checkout-summary-terms"]}
      </CheckboxLabel>
      {isPlacingOrder ? (
        <Loader />
      ) : (
        <Button
          id="save-order"
          onClick={handlePlaceOrder}
          text={translations[languageCode]["checkout-summary-place-order"]}
          backgroundColor={"var(--secondary-green)"}
          color="white"
          isFullWidth
          disabled={
            userRole == roles.CLIENT && userId && Number(userId) > 0
              ? false
              : true
          }
        />
      )}
      <ToastContainer />
    </Container>
  );
};

export default CheckoutSummary;
